import { banner, profile } from "../assets";
import { getOrganizationLogo } from "../assets/logos";
import HeroSection from "../components/HeroSection";

const QualificationsExperiencePage = () => {
    // Educational Qualifications in reverse chronological order (latest first)
    const qualifications = [
        {
            id: 1,
            degree: "Doctor of Philosophy (Ph.D.) – Jyotish (Sanskrit)",
            institution: "Shri Lal Bahadur Shastri Rashtriya Sanskrit Vidyapeeth",
            location: "New Delhi",
            year: "1999",
            grade: "Vidya-Varidhi",
            icon: "🎓"
        },
        {
            id: 2,
            degree: "National Eligibility Test (NET) – Jyotish (Sanskrit)",
            institution: "University Grants Commission (UGC)",
            location: "India",
            year: "1997",
            grade: "Qualified",
            icon: "📜"
        },
        {
            id: 3,
            degree: "Master of Arts (M.A.) – Sanskrit (Acharya), Phalit Jyotish",
            institution: "Shri Lal Bahadur Shastri Rashtriya Sanskrit Vidyapeeth",
            location: "New Delhi",
            year: "1993",
            grade: "Acharya",
            icon: "📚"
        },
        {
            id: 4,
            degree: "Bachelor of Education (B.Ed.) – Sanskrit & Hindi (Shiksha Shastri)",
            institution: "Rashtriya Sanskrit Sansthan",
            location: "New Delhi",
            year: "1991",
            grade: "Shiksha Shastri",
            icon: "🎯"
        },
        {
            id: 5,
            degree: "Master of Arts (M.A.) – Sanskrit (Acharya), Darshan",
            institution: "Himachal Pradesh University",
            location: "Shimla",
            year: "1989",
            grade: "Acharya",
            icon: "📖"
        },
        {
            id: 6,
            degree: "Bachelor of Arts (B.A. Hons.) – Sanskrit (Visishta Shastri)",
            institution: "Himachal Pradesh University",
            location: "Shimla",
            year: "1987",
            grade: "Visishta Shastri",
            icon: "📋"
        }
    ];

    // Teaching Experience
    const teachingExperience = [
        {
            position: "Vice Chancellor",
            department: "Sampurnanand Sanskrit University",
            institution: "Sampurnanand Sanskrit University",
            location: "Varanasi",
            period: "Aug 2023 - Present",
            duration: "2+ years",
            description: "Leading one of the world's largest Sanskrit universities with a 234+ year legacy, elevating it into an international bastion of learning."
        },
        {
            position: "Professor",
            department: "Department of Jyotish (Sanskrit)",
            institution: "Shri Lal Bahadur Shastri Rastriya Sanskrit Vidyapeetha",
            location: "New Delhi",
            period: "Feb 2012 - Present",
            duration: "14+ years",
            description: "Teaching and Research experience as Professor"
        },
        {
            position: "Associate Professor",
            department: "Department of Jyotish (Sanskrit)",
            institution: "Shri Lal Bahadur Shastri Rastriya Sanskrit Vidyapeetha",
            location: "New Delhi",
            period: "Feb 2009 - Feb 2012",
            duration: "3 years",
            description: "Teaching and Research experience"
        },
        {
            position: "Reader",
            department: "Department of Jyotish (Sanskrit)",
            institution: "Shri Lal Bahadur Shastri Rastriya Sanskrit Vidyapeetha",
            location: "New Delhi",
            period: "08 Feb 2006 - 08 Feb 2009",
            duration: "3 years",
            description: "Teaching and Research experience"
        },
        {
            position: "Senior Lecturer",
            department: "Department of Jyotish (Sanskrit)",
            institution: "Shri Lal Bahadur Shastri Rastriya Sanskrit Vidyapeetha",
            location: "New Delhi",
            period: "Nov 2001 - Feb 2006",
            duration: "5 years",
            description: "Teaching and research experience"
        },
        {
            position: "Lecturer",
            department: "Department of Jyotish (Sanskrit)",
            institution: "Shri Lal Bahadur Shastri Rastriya Sanskrit Vidyapeetha",
            location: "New Delhi",
            period: "Nov 1997 - Nov 2001",
            duration: "4 years",
            description: "Teaching experience"
        }
    ];

    return (
        <div className="min-h-screen bg-secondary-50">
            <HeroSection bgImage={banner} frontImage={profile}>
                <div className="text-left space-y-4 lg:space-y-6">
                    <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-heritage-ivory leading-tight">
                        Qualifications & Experience
                    </h1>
                    
                    <h2 className="text-xl lg:text-2xl xl:text-3xl font-sans font-semibold text-heritage-gold">
                        Academic Credentials & Professional Teaching Journey
                    </h2>
                    
                    <p className="text-lg lg:text-xl text-heritage-ivory/95 font-serif leading-relaxed">
                        Comprehensive overview of educational qualifications and 29+ years of 
                        distinguished teaching experience in Sanskrit and Jyotish studies.
                    </p>
                </div>
            </HeroSection>

            <div className="container mx-auto px-4 max-w-7xl py-16">
                {/* Educational Qualifications */}
                <section className="mb-16">
                    <h2 className="text-display-md font-serif text-heritage-maroon mb-8 text-center">
                        Educational Qualifications
                    </h2>
                    <div className="space-y-6">
                        {qualifications.map((qualification) => (
                            <div 
                                key={qualification.id}
                                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 hover:scale-105 flex items-center"
                            >
                                <div className="text-4xl mr-6">
                                    {(() => {
                                        const logo = getOrganizationLogo(qualification.institution);
                                        if (typeof logo === 'object' && logo.type === 'image') {
                                            return <img src={logo.src} alt={logo.alt} className="w-12 h-12 object-contain"/>;
                                        }
                                        return logo || qualification.icon;
                                    })()}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-heading-md font-serif text-heritage-maroon mb-2">
                                        {qualification.degree}
                                    </h3>
                                    <div className="text-primary-600 font-semibold mb-1">
                                        {qualification.institution}
                                    </div>
                                    <div className="text-secondary-500 text-sm">
                                        {qualification.location} • {qualification.year} • {qualification.grade}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Teaching Experience */}
                <section className="mb-16">
                    <h2 className="text-display-md font-serif text-heritage-maroon mb-8 text-center">
                        Professional Experience (29+ Years)
                    </h2>
                    <div className="space-y-6">
                        {teachingExperience.map((experience, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 hover:scale-105"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center">
                                        <div className="text-3xl mr-4">
                                            {(() => {
                                                const logo = getOrganizationLogo(experience.institution);
                                                if (typeof logo === 'object' && logo.type === 'image') {
                                                    return <img src={logo.src} alt={logo.alt} className="w-12 h-12 object-contain"/>;
                                                }
                                                return logo || "🏛️";
                                            })()}
                                        </div>
                                        <h3 className="text-heading-md font-serif text-heritage-maroon">
                                            {experience.position}
                                        </h3>
                                    </div>
                                    <span className="bg-accent-100 text-nowrap text-accent-700 px-3 py-1 rounded-full text-sm font-semibold">
                                        {experience.duration}
                                    </span>
                                </div>
                                
                                <div className="text-primary-600 font-semibold mb-1">
                                    {experience.department}
                                </div>
                                
                                <div className="text-secondary-600 font-medium mb-2">
                                    {experience.institution}, {experience.location}
                                </div>
                                
                                <div className="text-secondary-500 text-sm mb-3">
                                    {experience.period}
                                </div>
                                
                                <p className="text-secondary-600 leading-relaxed text-sm">
                                    {experience.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default QualificationsExperiencePage;
